import * as React from 'react';
import { useEffect, useState } from 'react';
import DomainIcon from '@material-ui/icons/Domain';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { AnchorListItem } from './AnchorListItem';
import { AppName } from '@pec/aion-ui-core/interfaces/appName';
import { CommonRootState } from '@pec/aion-ui-core/combineReducers';
import { DeepReadonly } from 'utility-types';
import { Feature } from 'flagged';
import { FeatureFlagWrapper } from '@pec/aion-ui-core/features/featureFlags/components/FeatureFlagWrapper';
import { useFeature } from 'flagged';
import { InlineBadge } from '../InlineBadge';
import { IOrganization, OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { IOrganizationProfileStatus } from '@pec/aion-ui-core/interfaces/organizationProfileNotifications';
import { ListSubheader } from './ListSubheader';
import { NavLinkListItem } from './NavLinkListItem';
import { OrganizationProfileSection } from './OrganizationProfileSection';
import { PECEnv } from '@pec/aion-ui-core/interfaces/serverTokens';
import { SectionContainer } from '../../containers/Nav/Section';
import { Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import { Trans, useTranslation } from 'react-i18next';
import { useOrganizationProfileNotifications } from '../../hooks/useOrganizationProfileNotifications';
import { useProgramReviewsBadge } from '../../hooks/useProgramReviewsBadge';
import { useSelector } from 'react-redux';
const {
  Client,
  SupplyChainV2,
  LinkVeriforceCompany,
  SamlSso,
  GlobalWorkerPass,
  DovetailCohortZero,
  ManageHiringClientConnections,
  ChasOrganizationFields
} = OrganizationFeature;
const { Read, Write, Manage } = ActivityAction;
const { REACT_APP_NAME } = process.env;
const { AionLinksUI, AionSafetyProgramsUI, AionSubscriberUI } = AppName;
const {
  CustomerSupport,
  Organizations,
  QuestionnaireSections,
  SafetyProgramAnswers,
  SafetyProgramEvaluations,
  SamlSso: SamlSsoActivityResource,
  Subscriptions,
  SupplyChain,
  Users,
  Verifications,
  VeriforceOrganizationLinks
} = ActivityResourceName;

const styles = (theme: Theme) => ({
  legacySsqId: {
    color: theme.palette.primary.main
  }
});

type OwnProps = {
  hasGlobalPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  hasOrganizationPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  organization: DeepReadonly<IOrganization>;
  promptForOrganizationLink: boolean;
  isChasClient?: boolean;
  ssqId: string;
  organizationProfileNotifications?: IOrganizationProfileStatus;
};

export type Props = WithStyles<typeof styles> & OwnProps;

const UserOrganizationSectionComponent: React.FC<Props> = ({
  organization,
  promptForOrganizationLink,
  ssqId,
  hasGlobalPermission,
  hasOrganizationPermission,
  classes,
  organizationProfileNotifications,
  isChasClient
}) => {
  const { id: organizationId, features } = organization;
  const { serverTokens } = useSelector((state: CommonRootState) => state.serverTokens);
  const userInfo = useSelector((state: CommonRootState) => state.userInfo.userInfo);
  const [globalWorkerPassUrl, setGlobalWorkerPassUrl] = useState('');
  const { statuses, isProfileCompleted } = useOrganizationProfileNotifications();

  const isToDoListEnabled = useFeature('AionShowToDoListOnSubscriberUI');

  const shouldFetchProgramReviews = !isChasClient && !features.includes(ChasOrganizationFields);
  const { count: programReviewsCount } = shouldFetchProgramReviews
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useProgramReviewsBadge(userInfo?.primaryOrganizationId || '', organizationId)
    : { count: 0 };

  useEffect(() => {
    const { beta, dev, production } = PECEnv;
    const globalWorkerPassUrlMap = {
      [dev]: 'https://wp.qa-veriforceone.com/gwp/com.vfwp.site.loginveriforceone',
      [beta]: 'https://wp.beta-veriforceone.com/gwp/com.vfwp.site.loginveriforceone',
      [production]: 'https://wp.veriforceone.com/gwp/com.vfwp.site.loginveriforceone'
    };
    if (serverTokens) {
      setGlobalWorkerPassUrl(globalWorkerPassUrlMap[serverTokens.pecEnv] || globalWorkerPassUrlMap[dev]);
    }
  }, [serverTokens]);

  const hasAdminFeatures =
    hasOrganizationPermission(Read, CustomerSupport) ||
    hasOrganizationPermission(Write, Organizations) ||
    hasOrganizationPermission(Manage, QuestionnaireSections) ||
    hasOrganizationPermission(Manage, Subscriptions) ||
    hasOrganizationPermission(Manage, Verifications);

  const isCohortZero = features.includes(DovetailCohortZero);

  const hasSupplyChainPermission =
    (hasOrganizationPermission(Read, SupplyChain) || hasGlobalPermission(Read, SupplyChain)) &&
    !features.includes(DovetailCohortZero);

  const hasSubscriptionsPermission =
    hasOrganizationPermission(Read, Subscriptions) || hasGlobalPermission(Read, Subscriptions);

  const userCanWriteVeriforceOrganizationLinks =
    features.includes(LinkVeriforceCompany) && hasOrganizationPermission(Write, VeriforceOrganizationLinks);
  const { t, i18n } = useTranslation();
  const isUsingEnglishUS = i18n.language === 'en-US';

  const membershipLinkText = isUsingEnglishUS
    ? t('components.userOrganizationSection.subscription', 'Subscription')
    : t('components.userOrganizationSection.membership', 'Membership');

  const hasNotifications = (userCanWriteVeriforceOrganizationLinks && promptForOrganizationLink) || !isProfileCompleted;
  const hasManageHiringClientsConnectionsFeature = features.includes(ManageHiringClientConnections);
  const isVisibleForNonChasOrganization =
    hasGlobalPermission(Manage, Organizations) || !features.includes(ChasOrganizationFields);
  const renderOrganizationProfileLink = (isEnabled: boolean) => {
    if (isEnabled && hasOrganizationPermission(Read, Organizations) && !features.includes(Client)) {
      return (
        <OrganizationProfileSection
          organizationId={organization.id}
          organizationProfileNotifications={organizationProfileNotifications}
          hasGlobalPermission={hasGlobalPermission}
          hasOrganizationPermission={hasOrganizationPermission}
          hasManageHiringClientsConnectionsFeature={hasManageHiringClientsConnectionsFeature}
          statuses={statuses}
        />
      );
    }

    if (
      !isEnabled &&
      hasOrganizationPermission(Write, Organizations) &&
      !hasOrganizationPermission(Manage, Organizations)
    ) {
      return REACT_APP_NAME === AionSubscriberUI ? (
        <NavLinkListItem inset to={`/${organizationId}/questionnaire/1`}>
          {t('components.userOrganizationSection.companyProfile', 'Company Profile')}
        </NavLinkListItem>
      ) : (
        <AnchorListItem inset href={`/organizations/${organizationId}/questionnaire/1`}>
          {t('components.userOrganizationSection.companyProfile', 'Company Profile')}
        </AnchorListItem>
      );
    }

    return null;
  };

  return (
    <SectionContainer
      id="UserOrganization"
      tooltip
      subheader={organization.name}
      icon={<DomainIcon />}
      hasNotifications={hasNotifications}
      isBold={true}
    >
      <ListSubheader inset>
        <Trans i18nKey="components.userOrganizationSection.ssqId">
          SSQ ID: <span className={classes.legacySsqId}>{{ ssqId }}</span>
        </Trans>
      </ListSubheader>
      {!isChasClient ? (
        features.includes(Client) ? (
          <AnchorListItem inset href="/SSQV4/SSQV5/Home/OperatorHome">
            {t('components.userOrganizationSection.companyDashboard', 'Company Dashboard')}
          </AnchorListItem>
        ) : !window.location.href.includes('/membership') &&
          (REACT_APP_NAME === AionLinksUI || REACT_APP_NAME === AionSubscriberUI) ? (
          <NavLinkListItem inset to={`/${organizationId}`}>
            {t('components.userOrganizationSection.companyDashboard', 'Company Dashboard')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${organizationId}`}>
            {t('components.userOrganizationSection.companyDashboard', 'Company Dashboard')}
          </AnchorListItem>
        )
      ) : null}
      {((hasGlobalPermission(Manage, SafetyProgramAnswers) || hasGlobalPermission(Manage, SafetyProgramEvaluations)) &&
        !features.includes(Client)) ||
      hasOrganizationPermission(Read, SafetyProgramAnswers) ? (
        !isChasClient && !features.includes(ChasOrganizationFields) ? (
          REACT_APP_NAME === AionSafetyProgramsUI ? (
            <NavLinkListItem inset to={`/organizations/${organizationId}/safety-program-requirements`}>
              <InlineBadge badgeContent={isToDoListEnabled ? programReviewsCount || undefined : undefined}>
                {t('components.userOrganizationSection.programReviews', 'Program Reviews')}
              </InlineBadge>
            </NavLinkListItem>
          ) : (
            <AnchorListItem inset href={`/organizations/${organizationId}/safety-program-requirements`}>
              <InlineBadge badgeContent={isToDoListEnabled ? programReviewsCount || undefined : undefined}>
                {t('components.userOrganizationSection.programReviews', 'Program Reviews')}
              </InlineBadge>
            </AnchorListItem>
          )
        ) : null
      ) : null}
      {features.includes(OrganizationFeature.ClientDashboardNavigation) && isChasClient && (
        <AnchorListItem inset href={`/organizations/${organizationId}/client-dashboard`}>
          {t('components.userOrganizationSection.dashboard', 'Dashboard')}
        </AnchorListItem>
      )}
      {hasOrganizationPermission(Manage, Users) && (
        <AnchorListItem inset href={`/organizations/${organizationId}/users`}>
          {t('components.userOrganizationSection.users', 'Users')}
        </AnchorListItem>
      )}
      {!isCohortZero && hasAdminFeatures && (
        <ListSubheader inset>{t('components.userOrganizationSection.companyAdmin', 'Company Admin')}</ListSubheader>
      )}
      {!isCohortZero && features.includes(Client) && hasOrganizationPermission(Write, Organizations) && !isChasClient && (
        <AnchorListItem inset href="/SSQV4/Admin/AdvancedCompanyInfo.aspx?btc=yes">
          {t('components.userOrganizationSection.aboutUs', 'About Us')}
        </AnchorListItem>
      )}
      {hasOrganizationPermission(Read, CustomerSupport) && (
        <AnchorListItem inset href={`/SSQV4/Dart/DartList.aspx?organizationId=${organizationId}`}>
          {t('components.userOrganizationSection.cms', 'CMS')}
        </AnchorListItem>
      )}
      {hasSubscriptionsPermission &&
        features.includes(ChasOrganizationFields) &&
        (REACT_APP_NAME === AionSubscriberUI && window.location.href.includes('/membership') ? (
          <NavLinkListItem inset to={`/${organizationId}/membership`}>
            {membershipLinkText}
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${organizationId}/membership`}>
            {membershipLinkText}
          </AnchorListItem>
        ))}
      {(hasOrganizationPermission(Manage, Organizations) || features.includes(Client)) && !isChasClient && (
        <AnchorListItem inset href={`/SSQV4/Admin/CompanyAdmin.aspx?organizationId=${organizationId}`}>
          {features.includes(Client)
            ? t('components.userOrganizationSection.companyProfile', 'Company Profile')
            : t('components.userOrganizationSection.companyProfileLegacy', 'Company Profile (legacy)')}
        </AnchorListItem>
      )}
      {renderOrganizationProfileLink(true)}
      {hasOrganizationPermission(Write, CustomerSupport) && !features.includes(Client) && (
        // TODO: CustomerSupport: Write doesn't really make sense here. We're in a rush and
        // simply using write to identify what was formerly called a 5938 user. We can't use
        // QuestionnaireSections:Write because contractors receive that permission for their
        // own organizations, but this link should only be accessible by PEC staff. A better
        // solution is to have separate resources for QuestionnaireSections (which
        // admins can edit) and QuestionnaireResponses (which contractors can edit).
        <AnchorListItem inset href={`/SSQV4/Admin/PECEditableSSQData.aspx?organizationId=${organizationId}`}>
          {t('components.userOrganizationSection.pecEditableQuestions', 'PEC Editable Questions')}
        </AnchorListItem>
      )}
      {(hasOrganizationPermission(Manage, SamlSsoActivityResource) ||
        hasGlobalPermission(Manage, SamlSsoActivityResource)) &&
        features.includes(SamlSso) && (
          <AnchorListItem inset href={`/identity/organizations/${organizationId}/saml-sso`}>
            {t('components.userOrganizationSection.samlSso', 'SAML SSO')}
          </AnchorListItem>
        )}
      {features.includes(GlobalWorkerPass) && (
        <AnchorListItem inset href={globalWorkerPassUrl}>
          {t('components.userOrganizationSection.workerPass', 'WorkerPass')}
        </AnchorListItem>
      )}
      {hasOrganizationPermission(Manage, Subscriptions) && (
        <AnchorListItem inset href={`/SSQV4/Admin/ManagePECSubscriptions.aspx?organizationId=${organizationId}`}>
          {t('components.userOrganizationSection.subscriptions', 'Subscriptions')}
        </AnchorListItem>
      )}
      {hasOrganizationPermission(Write, Organizations) && !isChasClient && isVisibleForNonChasOrganization && (
        <AnchorListItem inset href={'/SSQV4/ThirdParty/Configuration.aspx'}>
          {t('components.userOrganizationSection.thirdPartyServices', 'Third-Party Services')}
        </AnchorListItem>
      )}
      {hasOrganizationPermission(Manage, Verifications) && (
        <AnchorListItem inset href={`/SSQV4/Admin/CertifySSQ.aspx?organizationId=${organizationId}`}>
          {t('components.userOrganizationSection.verifications', 'Verifications')}
        </AnchorListItem>
      )}
      {features.includes(LinkVeriforceCompany) && hasOrganizationPermission(Write, VeriforceOrganizationLinks) && (
        <FeatureFlagWrapper>
          <Feature
            name={'HideLinkVeriforceCompany'}
            render={(isEnabled: boolean) => (
              <AnchorListItem inset href={isEnabled ? undefined : `/veriforce-integration/${organizationId}`}>
                <InlineBadge badgeContent={Number(promptForOrganizationLink)}>
                  {t('components.userOrganizationSection.veriSource', 'VeriSource')}
                </InlineBadge>
              </AnchorListItem>
            )}
          />
        </FeatureFlagWrapper>
      )}
      {[Client, SupplyChainV2].every(value => features.includes(value)) && hasSupplyChainPermission && (
        <FeatureFlagWrapper>
          <Feature name={'AionPreQualificationGroupsUI'}>
            <AnchorListItem inset href={`/organizations/${organizationId}/supply-chains-v2`}>
              {t('components.userOrganizationSection.supplyChains', 'Supply Chains')}
            </AnchorListItem>
          </Feature>
        </FeatureFlagWrapper>
      )}
    </SectionContainer>
  );
};

export const UserOrganizationSection = withStyles(styles, { withTheme: true })(UserOrganizationSectionComponent);
